import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackendConnector from '../Utils/BackendConnector';
import Constant from '../Utils/Constant';
import Error500Page from '../Error500/Error500Page';
import NotFoundPage from '../NotFound/NotFoundPage';
import { usePhotoCarousel } from '../Utils/usePhotoCarousel';
import { processError } from '../Utils/ErrorHandler';
import { createSafeHTML } from '../Utils/HTMLSanitizer';
import { renderDescriptionWithPhotos } from '../Utils/PhotoRenderer';
import { FaArrowLeft, FaExternalLinkAlt, FaImages, FaMapMarkerAlt, FaRegCalendar } from 'react-icons/fa';

const HikingPage = () => {
    const { hikingId } = useParams();
    const navigate = useNavigate();
    const [hiking, setHiking] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    
    // Carousel hook provides all carousel state and functions
    const { carouselOpen, carouselIndex, carouselPhotos, carouselRef, handleTouchStart, handleTouchMove, handleTouchEnd, openCarousel, closeCarousel, prevPhoto, nextPhoto, setCarouselPhotos } = usePhotoCarousel();

    useEffect(() => {
        const fetchHikingData = async () => {
            try {
                setIsLoading(true);
                
                // Récupération des détails de la randonnée
                const hikingData = await BackendConnector.fetchData(`hikings/${hikingId}/`);
                
                if (!hikingData) {
                    throw new Error(Constant.HIKING_NOT_FOUND);
                }
                
                setHiking(hikingData);
                // Initialize carousel photos with hiking photos
                if (hikingData.photos && hikingData.photos.length > 0) {
                    setCarouselPhotos(hikingData.photos.sort((a, b) => a.order - b.order));
                }
                
            } catch (err) {
                console.error('Erreur lors du chargement de la randonnée:', err);
                const { error: errorMsg, status } = processError(err, Constant.UNABLE_TO_LOAD_HIKING);
                setErrorStatus(status);
                setError(errorMsg);
            } finally {
                setIsLoading(false);
            }
        };

        if (hikingId) {
            fetchHikingData();
        }
    }, [hikingId]);

    const handleBackClick = () => {
        navigate('/');
        setTimeout(() => {
            const treksElement = document.getElementById('treks');
            if (treksElement) {
                treksElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(Constant.DATE_LOCALE, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDuration = (hours) => {
        if (!hours) return null;
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return m > 0 ? `${h}h${m < 10 ? '0' : ''}${m}` : `${h}h`;
    };

    if (isLoading) {
        return (
            <div className="not-found-page">
                <div className="not-found-content">
                    <div className="spinner-border text-light" role="status" style={{fontSize: '2rem'}}>
                        <span className="visually-hidden">{Constant.LOADING_HIKING}</span>
                    </div>
                    <p className="not-found-message mt-3">{Constant.LOADING_HIKING}</p>
                </div>
            </div>
        );
    }

    if (error || !hiking) {
        // Si c'est une erreur 500, afficher la page d'erreur 500
        if (errorStatus === 500) {
            return <Error500Page />;
        }
        
        // Sinon, afficher la page 404 (pour les erreurs 404 ou autres)
        // Déterminer le message utilisateur à afficher (HIKING_NOT_FOUND si erreur 404, sinon le message d'erreur technique)
        let userMessage = Constant.HIKING_NOT_FOUND;
        let technicalMessage = null;
        if (error && (error.includes('404') || error.toLowerCase().includes('not found'))) {
            userMessage = Constant.HIKING_NOT_FOUND;
            technicalMessage = null;
        } else if (error) {
            userMessage = error;
            technicalMessage = error;
        }
        return (
            <NotFoundPage
                title={"404"}
                message={Constant.ERROR}
                description={userMessage}
                technicalMessage={technicalMessage}
                buttonLabel={Constant.BACK_TO_HIKINGS}
                onBack={handleBackClick}
                iconClass="fas fa-arrow-left me-2"
            />
        );
    }

    // Fiche technique : chiffre et unité séparés pour mettre le chiffre en avant
    const facts = [
        { label: Constant.DISTANCE, value: hiking.distance_km, unit: 'km' },
        { label: Constant.ELEVATION_GAIN, value: hiking.elevation_gain_m, unit: 'm' },
        { label: Constant.DURATION, value: formatDuration(hiking.duration_hours), unit: '' },
        { label: Constant.MAX_ALTITUDE, value: hiking.altitude_max_m, unit: 'm' },
    ].filter(fact => fact.value);

    return (
        <div className="hiking-page">
            {/* En-tête : photo, titre et informations pratiques */}
            <header
                className="hiking-hero"
                style={{
                    backgroundImage: hiking.hiking?.cover ? `url(${hiking.hiking.cover})` : 'none'
                }}
            >
                <div className="hiking-hero-inner">
                    <button className="hiking-back" onClick={handleBackClick}>
                        <FaArrowLeft aria-hidden="true" /> {Constant.BACK_TO_HIKINGS}
                    </button>

                    <div className="hiking-hero-content">
                        {hiking.tags && hiking.tags.length > 0 && (
                            <div className="hiking-tags-header">
                                {hiking.tags.map((tag) => (
                                    <span key={tag.id} className="hiking-tag-header">
                                        {tag.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        <h1 className="hiking-page-title">
                            {hiking.hiking?.name}
                        </h1>

                        {hiking.summary && (
                            <p className="hiking-lead">{hiking.summary}</p>
                        )}

                        <div className="hiking-meta">
                            <span className="hiking-meta-item">
                                <FaMapMarkerAlt aria-hidden="true" />
                                {hiking.hiking?.department}, {hiking.hiking?.country}
                            </span>
                            <span className="hiking-meta-item">
                                <FaRegCalendar aria-hidden="true" />
                                {formatDate(hiking.hiking?.date)}
                            </span>
                            {hiking.album && (
                                <button
                                    className="hiking-album-button"
                                    onClick={() => navigate(`/album/${hiking.album.id}`, {
                                        state: {
                                            from: 'hiking',
                                            hikingId: hikingId
                                        }
                                    })}
                                >
                                    <FaImages aria-hidden="true" />
                                    {Constant.SEE_ALBUM}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {facts.length > 0 && (
                <section className="hiking-facts" aria-label={Constant.HIKING_STATISTICS}>
                    <dl className="hiking-facts-list">
                        {facts.map(fact => (
                            <div className="hiking-fact" key={fact.label}>
                                <dt>{fact.label}</dt>
                                <dd>
                                    {fact.value}
                                    {fact.unit && <span className="hiking-fact-unit">{fact.unit}</span>}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </section>
            )}

            {/* Itinéraire : bandeau à part, distinct du récit */}
            {hiking.url_map && (
                <section className="hiking-route">
                    <div className="hiking-route-inner">
                        <div className="hiking-route-header">
                            <h2 className="hiking-section-title">{Constant.HIKING_ROUTE}</h2>
                            <a
                                className="hiking-map-link"
                                href={hiking.url_map}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {Constant.VIEW_MAP} <FaExternalLinkAlt aria-hidden="true" />
                            </a>
                        </div>
                        <div className="hiking-map-frame">
                            <div className="hiking-map-wrapper">
                                <iframe
                                    src={hiking.url_map}
                                    className="hiking-map-iframe"
                                    allowFullScreen
                                    loading="lazy"
                                    title={Constant.HIKING_ROUTE}
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <main className="hiking-body">
                <section className="hiking-story">
                    <h2 className="hiking-section-title">{Constant.HIKING_DESCRIPTION}</h2>
                    <div className="hiking-description-content">
                        {hiking.description ? (
                            renderDescriptionWithPhotos(
                                hiking.description,
                                hiking.photos,
                                openCarousel,
                                {
                                    rowClass: 'hiking-photos-row',
                                    itemClass: 'hiking-photo-item',
                                    photoClass: 'hiking-description-photo',
                                    textClass: 'hiking-text-content'
                                }
                            )
                        ) : (
                            <p className="article-in-progress">{Constant.ARTICLE_IN_PROGRESS}</p>
                        )}
                    </div>
                </section>
            </main>

            {/* Carrousel photos */}
            {carouselOpen && carouselPhotos?.length > 0 && (
                <div 
                    className="carousel-fullscreen"
                    ref={carouselRef}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <button 
                        className="carousel-close-btn"
                        onClick={closeCarousel}
                        aria-label="Fermer la galerie"
                        title="Fermer"
                    >
                        ✕
                    </button>

                    <div className="carousel-container">
                        <img 
                            src={carouselPhotos?.[carouselIndex]?.url} 
                            alt={`Photo ${carouselIndex + 1}`}
                            className="carousel-image"
                        />
                        
                        <button 
                            className="carousel-btn prev-btn"
                            onClick={prevPhoto}
                            aria-label="Photo précédente"
                            title="Photo précédente"
                        >
                            ←
                        </button>
                        
                        <button 
                            className="carousel-btn next-btn"
                            onClick={nextPhoto}
                            aria-label="Photo suivante"
                            title="Photo suivante"
                        >
                            →
                        </button>
                    </div>

                    <div className="carousel-controls">
                        <span className="carousel-counter">
                            {carouselIndex + 1} / {carouselPhotos?.length}
                        </span>
                    </div>
                    
                    {carouselPhotos?.[carouselIndex]?.title && (
                        <div className="carousel-title">
                            {carouselPhotos[carouselIndex].title}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HikingPage;